import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const EntitlementsContext = createContext({
  planId: 'free',
  status: 'active',
  entitlements: [],
  inTrial: false,
  expiresAt: null,
  loading: true,
  can: () => false,
});

export function useEntitlements() {
  return useContext(EntitlementsContext);
}

// Default entitlements per plan (client-side fallback)
// Tier names: curiosity (free), commitment (standard), determination (full)
const PLAN_ENTITLEMENTS = {
  // Free tier - limited access, no SRS
  free: [
    'episodes:1weekly',
    'exercises:limited',
    'vocabulary:view',
    'coach:limited',
    'notifications:daily',
  ],
  curiosity: [
    'episodes:1weekly',
    'exercises:limited',
    'vocabulary:view',
    'coach:limited',
    'notifications:daily',
  ],
  // Plus tier - 2 episodes, full SRS + adaptive
  plus: [
    'plus',
    'episodes:2weekly',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:daily',
    'notifications:streak',
    'graded-readers:2weekly',
  ],
  engagement: [
    'plus',
    'episodes:2weekly',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:daily',
    'notifications:streak',
    'graded-readers:2weekly',
  ],
  // Legacy pro tier - Unlimited episodes, full library, AI assistant
  pro: [
    'plus',
    'pro',
    'episodes:unlimited',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:all',
    'graded-readers:unlimited',
    'ai:assistant',
    'ai:coach',
    'ai:roleplay',
  ],
  // Commitment tier - 2 episodes/week, full exercise stack
  commitment: [
    'plus',
    'episodes:2weekly',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:daily',
    'notifications:streak',
    'graded-readers:2weekly',
  ],
  // Determination tier - full access, AI and unlimited content
  determination: [
    'plus',
    'pro',
    'episodes:unlimited',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:all',
    'graded-readers:unlimited',
    'ai:assistant',
    'ai:coach',
    'ai:roleplay',
  ],
  // Legacy plans - mapped to engagement tier
  lifetime: [
    'plus',
    'episodes:2weekly',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:daily',
    'notifications:streak',
    'graded-readers:2weekly',
  ],
  student: [
    'plus',
    'episodes:2weekly',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:daily',
    'notifications:streak',
    'graded-readers:2weekly',
  ],
  family: [
    'plus',
    'pro',
    'episodes:unlimited',
    'exercises:full',
    'vocabulary:view',
    'slp',
    'coach:full',
    'notes:unlimited',
    'comments:write',
    'discussions:post',
    'notifications:all',
    'graded-readers:unlimited',
    'ai:assistant',
    'ai:coach',
    'ai:roleplay',
  ],
};

function normalizePlanId(planIdRaw) {
  const key = String(planIdRaw || '').trim().toLowerCase();
  if (!key) return 'free';
  if (key === 'free' || key === 'curiosity') return key;
  if (key === 'plus' || key === 'engagement' || key === 'commitment' || key === 'lifetime' || key === 'student') return 'commitment';
  if (key === 'pro' || key === 'determination' || key === 'family') return 'determination';
  return key;
}

// Dev/test override for specific users - grants full access
const DEV_FULL_ACCESS_UIDS = [
  '0eNHdEz2R6XyD8ylp1E7eMqWLTr2', // guztag@gmail.com (owner)
  'SnqsdDZ8cOhaeeo4taIuYynUQ0G2', // gustavo.guajardo@nokut.no
  'I6y92W516CQJJI2XKrUJh60Tydu2', // info@aizeit.io (beta tester)
];

export function EntitlementsProvider({ children }) {
  const { currentUser } = useAuth();
  const [state, setState] = useState({
    planId: 'free',
    status: 'active',
    entitlements: [],
    trialEndsAt: null,
    expiresAt: null,
    loading: true,
  });

  useEffect(() => {
    if (!currentUser) {
      setState({
        planId: 'free',
        status: 'active',
        entitlements: PLAN_ENTITLEMENTS.free,
        trialEndsAt: null,
        expiresAt: null,
        loading: false,
      });
      return;
    }

    // Dev override: grant full access to specific users
    if (DEV_FULL_ACCESS_UIDS.includes(currentUser.uid)) {
      setState({
        planId: 'determination',
        status: 'active',
        entitlements: PLAN_ENTITLEMENTS.determination,
        trialEndsAt: null,
        expiresAt: null,
        loading: false,
      });
      return;
    }

    const subRef = doc(db, 'users', currentUser.uid, 'subscriptions', 'current');
    const unsub = onSnapshot(subRef, (snap) => {
      if (!snap.exists()) {
        setState((s) => ({ ...s, planId: 'free', status: 'active', entitlements: PLAN_ENTITLEMENTS.free, loading: false }));
        return;
      }
      const data = snap.data();
      const rawPlanId = data.planId || 'free';
      const planId = normalizePlanId(rawPlanId);
      const mergedEnts = Array.isArray(data.entitlements) && data.entitlements.length > 0
        ? data.entitlements
        : (PLAN_ENTITLEMENTS[planId] || PLAN_ENTITLEMENTS[rawPlanId] || PLAN_ENTITLEMENTS.free);
      setState({
        planId,
        status: data.status || 'active',
        entitlements: mergedEnts,
        trialEndsAt: data.trialEndsAt || null,
        expiresAt: data.expiresAt || null,
        loading: false,
      });
    }, () => {
      setState((s) => ({ ...s, loading: false }));
    });
    return () => unsub();
  }, [currentUser]);

  const value = useMemo(() => {
    const now = new Date();
    const inTrial = !!state.trialEndsAt && state.trialEndsAt.toDate ? (now < state.trialEndsAt.toDate()) : false;
    const ent = new Set(state.entitlements || []);
    const can = (feature) => ent.has(feature);
    return { ...state, inTrial, can };
  }, [state]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}
